import { useMutation, UseMutationOptions } from '@tanstack/vue-query';
import { GiveawayRequirementType, GiveawaySignupResponse } from '@manifoldxyz/redeem-client';
import { redeemApi } from '@/api/apiHelpers';
import { useClaimStore } from '@/store/claimStore';

type UsePatchSignupOptions = UseMutationOptions<
  { entry: GiveawaySignupResponse },
  unknown,
  { userData: any; index: number; type: GiveawayRequirementType },
  unknown
>;

type UseSignupOptions = UseMutationOptions<
  { entry: GiveawaySignupResponse },
  unknown,
  undefined,
  unknown
>;

export function usePatchSignup(options?: UsePatchSignupOptions) {
  const store = useClaimStore();

  const {
    mutate: patch,
    mutateAsync: patchAsync,
    ...rest
  } = useMutation(
    async ({
      userData,
      index,
      type,
    }: {
      userData: any;
      index: number;
      type: GiveawayRequirementType;
    }) => {
      let entry: GiveawaySignupResponse | undefined = store.signup;
      // Check if entry already created for this user, create if so
      if (!store.signup) {
        entry = await redeemApi.clientLib.createSignup(store.id);
      }

      if (!entry) {
        throw new Error('Could not create entry');
      }

      await redeemApi.clientLib.patchSignup(store.id, false, [
        {
          index,
          userData,
          type,
        },
      ]);

      return { entry };
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        store.refreshSignup(true);
        options?.onSuccess?.(data, variables, context);
      },
      onError: (err, variables, context) => {
        options?.onError?.(err, variables, context);
      },
    }
  );

  return {
    ...rest,
    patch,
    patchAsync,
  };
}

export function useSignup(options?: UseSignupOptions) {
  const store = useClaimStore();

  const {
    mutate: signup,
    mutateAsync: signupAsync,
    ...rest
  } = useMutation(
    async () => {
      let entry: GiveawaySignupResponse | undefined = store.signup;

      // Check if entry already created for this user, create if so
      if (!store.signup) {
        entry = await redeemApi.clientLib.createSignup(store.id);
      }

      if (!entry) {
        throw new Error('Could not create entry');
      }

      if (store.flatRequirements.some((requirement) => requirement && !requirement.complete)) {
        throw new Error('All requirements must be completed');
      }

      await redeemApi.clientLib.patchSignup(store.id, true);

      return { entry };
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        store.refreshSignup(true);
        options?.onSuccess?.(data, variables, context);
      },
      onError: (err, variables, context) => {
        options?.onError?.(err, variables, context);
      },
    }
  );

  return {
    ...rest,
    signup,
    signupAsync,
  };
}
